import { NodeApi } from 'platejs';
import z from 'zod';

import { Attachment } from '@@api/services/attachments/schemas';
import { CropMarks, Point } from '@@api/utils/schemas/schemas';
import IMAGE_MIME_TYPES from '@@constants/ImageMimeTypes';

import { Caption, type CaptionElement } from './Caption';
import { Credit, type CreditElement } from './Credit';
import { ParagraphCodec, UnityParagraph } from './Paragraph';
import { TextElementsCodec } from './Text';
import { CURRENT_VERSION, ElementId, UnityElementId, Version } from '../commons';
import { ELEMENT_TYPES, NameSource } from '../constants';

const Asset = z.object({
    id: z.string(),
    assetType: z.string(),
    previewUrl: z.string(),
    metadata: z.object({
        filename: z.string(),
        folderPath: z.string(),
    }),
});

const ImageElementOverlayIconType = {
    Lesereporter: '__image-icon-leserreporter',
    Newsticker: '__image-icon-newsticker',
    Video: '__image-icon-video',
} as const;

const ImageElementOverlayIconPosition = z.union([
    z.literal('bottom-left'),
    z.literal('bottom-right'),
    z.literal('top-left'),
    z.literal('top-right'),
]);

const OverlayIcon = z.object({
    type: z.enum(ImageElementOverlayIconType),
    position: ImageElementOverlayIconPosition.nullable(),
});

const ImageStyle = z
    .object({
        imageType: z.union([z.literal('image'), z.literal('infographic')]),
    })
    .optional();

export const UnityImage = z.object({
    type: z.literal(ELEMENT_TYPES.IMAGE),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    src: z.string().nullish(),
    originalSrc: z.string().nullish(),
    contentAttributes: z.object({
        caption: z
            .object({
                richTextFormat: z.array(UnityParagraph),
            })
            .nullable(),
        credit: z.string().nullable(),
        name: z.string().nullish(),
        nameSource: z.enum(NameSource).nullish(),
        elvisId: z.string().nullish(),
        url: z.url().nullish(),
        cropMarks: CropMarks.nullish(),
        focusPoint: Point.nullish(),
        overlayIcon: OverlayIcon.nullish(),
        naturalWidth: z.number().nullish(),
        naturalHeight: z.number().nullish(),
        style: ImageStyle.nullish(),
    }),
    mimetype: z.enum(IMAGE_MIME_TYPES).nullable(),
});
export type UnityImage = z.infer<typeof UnityImage>;

export const PlateImage = z.object({
    type: z.literal(ELEMENT_TYPES.IMAGE),
    id: ElementId,
    data: z.object({
        templateElement: z.boolean().optional(),
        src: z.string().nullish(),
        originalSrc: z.union([z.file(), z.url(), Attachment, Asset]).nullish(),
        mimetype: z.enum(IMAGE_MIME_TYPES).nullish(),
        embed: z
            .object({
                url: z.url().nullish(),
                elvisId: z.string().nullish(),
                name: z.string().nullish(),
                nameSource: z.enum(NameSource).nullish(),
                cropMarks: CropMarks.nullish(),
                focusPoint: Point.nullish(),
                naturalWidth: z.number().nullish(),
                naturalHeight: z.number().nullish(),
                style: ImageStyle.nullish(),
            })
            .optional(),
    }),
    children: z.tuple([Caption, Credit]),
});
export type ImageElement = z.infer<typeof PlateImage>;

export const ImageCodec = z.codec(UnityImage, PlateImage, {
    decode: (value) => {
        const isTemplateElement = value.templateElement ?? false;

        const captionItems = value.contentAttributes.caption?.richTextFormat[0]?.items;
        const hasCaptionContent = Boolean(
            captionItems?.some((item) => 'text' in item && item.text !== ''),
        );
        const hasCreditContent = Boolean(value.contentAttributes.credit);

        const caption: CaptionElement = {
            type: ELEMENT_TYPES.CAPTION,
            id:
                value.contentAttributes.caption?.richTextFormat[0]?.elementId ??
                crypto.randomUUID(),
            ...(isTemplateElement && !hasCaptionContent && { data: { templateElement: true } }),
            children: captionItems ? TextElementsCodec.decode(captionItems) : [{ text: '' }],
        };

        const credit: CreditElement = {
            type: ELEMENT_TYPES.CREDIT,
            ...(isTemplateElement && !hasCreditContent && { data: { templateElement: true } }),
            children: [{ text: value.contentAttributes.credit ?? '' }],
        };

        if (value.templateElement || value.src == null) {
            return {
                type: value.type,
                id: value.elementId ?? crypto.randomUUID(),
                data: {
                    templateElement: isTemplateElement,
                    src: null,
                    originalSrc: null,
                },
                children: [caption, credit] satisfies [CaptionElement, CreditElement],
            };
        }

        return {
            type: value.type,
            id: value.elementId ?? crypto.randomUUID(),
            data: {
                templateElement: value.templateElement,
                src: value.src,
                originalSrc: null,
                mimetype: value.mimetype,
                embed: {
                    url: value.contentAttributes.url,
                    elvisId: value.contentAttributes.elvisId,
                    name: value.contentAttributes.name,
                    nameSource: value.contentAttributes.nameSource,
                    cropMarks: value.contentAttributes.cropMarks,
                    focusPoint: value.contentAttributes.focusPoint,
                    naturalWidth: value.contentAttributes.naturalWidth,
                    naturalHeight: value.contentAttributes.naturalHeight,
                    style: value.contentAttributes.style,
                },
            },
            children: [caption, credit] satisfies [CaptionElement, CreditElement],
        };
    },
    encode: (value) => {
        const caption = ParagraphCodec.encode({
            ...value.children[0],
            type: ELEMENT_TYPES.PARAGRAPH,
        });

        const credit = NodeApi.string(value.children[1]);

        if (value.data.templateElement || value.data.src == null) {
            return {
                type: value.type,
                elementId: value.id,
                version: CURRENT_VERSION,
                templateElement: value.data.templateElement,
                src: null,
                originalSrc: null,
                mimetype: null,
                contentAttributes: {
                    caption: {
                        richTextFormat: [caption],
                    },
                    credit,
                },
            };
        }

        return {
            type: value.type,
            elementId: value.id,
            version: CURRENT_VERSION,
            templateElement: value.data.templateElement,
            src: value.data.src,
            originalSrc: null,
            mimetype: value.data.mimetype ?? null,
            contentAttributes: {
                ...value.data.embed,
                caption: {
                    richTextFormat: [caption],
                },
                credit,
            },
        };
    },
});
